import { getDuration } from '@common-fe/common-fe';
import { AxiosRequestConfig } from 'axios';
import  {jwtDecode} from 'jwt-decode';
import { toNumber } from 'lodash';

import { Roles } from '@/common/constants';

import { clearLocalStorageExceptSomeKeys } from '../modifiers/clearLocalStorageExceptSomeKeys';

//remove after BE will be send CLOSE_CIP_STATUS
const CLOSE_CIP_KEY = 'CLOSE_CIP:HIDDEN-';

interface RefreshToken {
  'custom:permissions': string,
  'custom:role': Roles,
  'custom:contact_id': string;
  'custom:organization_path': string;
  'custom:organization_id': string;
}

export const LOCAL_STORAGE_TOKEN_KEY = 'token';
export const LOCAL_STORAGE_REFRESH_TOKEN_KEY = 'refresh_token';
export const LOCAL_STORAGE_EXPIRES_IN = 'expires_in';
export const LOCAL_USER_NAME = 'user_name';
export const AUTH_HEADER_PREFIX = 'Bearer';
export const ACCESS_TOKEN_KEY = 'id_token';
export const IS_SSO_TOKEN = 'IS_SSO_TOKEN';
export const LOCAL_STORAGE_SSO_EXPIRES_IN = 'sso_expires_in';
export const LOCAL_STORAGE_SSO_EXPIRES_IN_TIMESTAMP = 'sso_expires_in_timestamp';
const TOKEN_REFRESH_BUFFER_SECONDS = 10;

export default class Token {
  public static isAuthenticated(): boolean {
    return !!this.getToken();
  }

  public static getAuthentication(): AxiosRequestConfig {
    const token = this.getToken();
    if (!token) {
      return {};
    }
    return {
      headers: { Authorization: `${AUTH_HEADER_PREFIX} ${token}` },
    };
  }

  public static storeToken(token: string): void {
    localStorage.setItem(LOCAL_STORAGE_TOKEN_KEY, token);
  }

  public static storeRefreshToken(refreshToken: string): void {
    localStorage.setItem(
      LOCAL_STORAGE_REFRESH_TOKEN_KEY,
      refreshToken,
    );
  }

  public static storeExpiresIn(expiresIn: number): void {
    localStorage.setItem(LOCAL_STORAGE_EXPIRES_IN, `${expiresIn}`);
  }

  public static storeSsoExpiresIn(expiresIn: number): void {
    localStorage.setItem(LOCAL_STORAGE_SSO_EXPIRES_IN_TIMESTAMP, `${Date.now() + getDuration({ seconds: toNumber(expiresIn - TOKEN_REFRESH_BUFFER_SECONDS) })}`);
    localStorage.setItem(LOCAL_STORAGE_SSO_EXPIRES_IN, `${expiresIn}`);
  }

  public static storeUserName(userName: string): void {
    localStorage.setItem(LOCAL_USER_NAME, userName);
  }

  public static clear(): void {
    // localStorage.clear();
    clearLocalStorageExceptSomeKeys({partOfKeyToKeep: CLOSE_CIP_KEY});
    localStorage.removeItem(LOCAL_STORAGE_TOKEN_KEY);
    localStorage.removeItem(LOCAL_STORAGE_REFRESH_TOKEN_KEY);
  }

  public static getUserName(): string | null {
    return localStorage.getItem(LOCAL_USER_NAME);
  }

  public static getRefreshToken(): string | null {
    return localStorage.getItem(LOCAL_STORAGE_REFRESH_TOKEN_KEY);
  }

  public static getToken() {
    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);
    let accessToken = urlParams.get(ACCESS_TOKEN_KEY);
    let expiresIn;

    const { hash, search, pathname } = window.location;
    if (!accessToken) {
      const accessTokenPart = hash.split('&').find((part) => part.startsWith(ACCESS_TOKEN_KEY)) || '';
      const expiresInPart = hash.split('&').find((part) => part.startsWith(LOCAL_STORAGE_EXPIRES_IN)) || '';
      accessToken = accessTokenPart.split('=')[1];
      expiresIn = expiresInPart.split('=')[1];
    }
    if (accessToken) {
      localStorage.setItem(LOCAL_STORAGE_TOKEN_KEY, accessToken);
      if (expiresIn) {
        Token.storeSsoExpiresIn(toNumber(expiresIn));
      }
      localStorage.setItem(IS_SSO_TOKEN, 'true');
      window.location.replace(pathname + search);
      return accessToken;
    }
    return localStorage.getItem(LOCAL_STORAGE_TOKEN_KEY);
  }

  public static setSSOToken(token?: string, expiresIn?: string) {
    if (token) {
      localStorage.setItem(LOCAL_STORAGE_TOKEN_KEY, token);
      localStorage.setItem(IS_SSO_TOKEN, 'true');
    }
    if (expiresIn) {
      Token.storeSsoExpiresIn(toNumber(expiresIn));
    }
  }

  public static isSsoExpired(): boolean {
    const expiresAt = localStorage.getItem(LOCAL_STORAGE_SSO_EXPIRES_IN_TIMESTAMP);
    return expiresAt ? Date.now() >= Number(expiresAt) : false;
  }

  public static getSSODataFromHash() {
    const { hash } = window.location;
    const accessTokenPart = hash.split('&').find((part) => part.startsWith(ACCESS_TOKEN_KEY)) || '';
    const accessToken = accessTokenPart.split('=')[1];
    const expiresInPart = hash.split('&').find((part) => part.startsWith(LOCAL_STORAGE_EXPIRES_IN)) || '';
    const expiresIn = expiresInPart.split('=')[1];
    return accessToken ? { accessToken, expiresIn } : undefined;
  }

  public static getDecodedToken(): RefreshToken | null {
    const token = Token.getToken();
    if (!token) {
      return null;
    }
    return jwtDecode(token);
  }
}
